import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import JobCard from '../../schedule/job-cards/JobCard';
import JobBadges from '../../schedule/indicators/JobBadges';
import EditButton from '../../schedule/indicators/EditButton';
import JobsColumn from '../../schedule/layout/JobsColumn';

export default function MyJobsColumn({
  myPendingJobs = {},
  jobsLoading = false,
  isLoadingMore = false,
  onJobEdit,
  onJobViewDetails,
  canEditJobs = false,
  hasMore = false,
  handleLoadMore = () => {},
  searchTerm = '',
  totalLoadedJobs = 0,
  isSelectionMode = false,
  selectedJobIds = new Set(),
  onJobSelect = () => {}
}) {
  // Reference to job list container for scroll handling
  const jobListRef = useRef(null);
  
  // All pending jobs in a flat array for reference
  const allPendingJobs = Object.values(myPendingJobs).flat();
  
  // Render standard action buttons for a job (only when not in selection mode)
  const renderJobActions = (job) => {
    if (isSelectionMode) return null;
    
    return (
      <>
        <button
          onClick={() => onJobViewDetails && onJobViewDetails(job.id)}
          className="inline-flex items-center px-3 py-1.5 border border-blue-500 text-xs font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 whitespace-nowrap"
        >
          View Details
          <svg className="ml-1.5 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>
        
        {canEditJobs && <EditButton onClick={() => onJobEdit(job)} />}
      </>
    );
  };

  // Handle job selection
  const handleJobSelect = (jobId, isSelected) => {
    onJobSelect(jobId, isSelected);
  };

  // Render each job card
  const renderPendingJobCard = (job, index) => {
    const isSelected = selectedJobIds.has(job.id);
    
    return (
      <JobCard
        key={job.id}
        job={job}
        index={index}
        onJobViewDetails={isSelectionMode ? undefined : onJobViewDetails}
        renderBadges={(job) => <JobBadges job={job} allJobs={allPendingJobs} />}
        renderActions={(job) => renderJobActions(job)}
        isSelectionMode={isSelectionMode}
        isSelected={isSelected}
        onSelect={handleJobSelect}
      />
    );
  };

  // Helper to render the loading indicator
  const renderLoadingIndicator = () => (
    <div className="py-2 text-center">
      <div className="inline-flex items-center px-4 py-2 bg-white shadow-sm rounded-md">
        <svg className="animate-spin h-5 w-5 text-blue-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading more jobs...
      </div>
    </div>
  );

  // Determine if we're in search mode
  const isSearching = searchTerm && searchTerm.length >= 2;
  
  // Calculate total pending jobs
  const totalPendingJobs = allPendingJobs.length;
  
  // Check if we have any pending jobs to display
  const hasPendingJobs = Object.keys(myPendingJobs).length > 0;
  
  // Define appropriate loading states
  const isInitialLoading = jobsLoading && !hasPendingJobs;

  return (
    <div className="flex flex-col">
      <JobsColumn
        title="Jobs To Complete"
        jobsByDate={myPendingJobs}
        loading={isInitialLoading}
        renderJobCard={renderPendingJobCard}
        badgeColor="bg-blue-100 text-blue-800"
        spinnerColor="border-sky-600"
        emptyStateProps={{
          title: "No pending jobs",
          description: isInitialLoading 
            ? "Loading jobs..." 
            : searchTerm 
              ? `No jobs match your search for "${searchTerm}"`
              : "You have no pending jobs assigned to you",
          icon: "calendar"
        }}
        containerClassName="h-[800px]"
        contentClassName="job-list-container overflow-y-auto h-[750px]"
        defaultExpanded={true}
        totalCount={totalPendingJobs}
        ref={jobListRef}
        showCounter={false}
        // Disable accordion functionality by always keeping expanded
        isAlwaysExpanded={true}
      />
      
      {/* Loading indicator - only show when loading more */}
      {isLoadingMore && (
        <div className="mt-2 py-2 text-center">
          {renderLoadingIndicator()}
        </div>
      )}
      
      {/* Load more button */}
      {hasMore && !isLoadingMore && !isInitialLoading && (
        <div className="mt-4 text-center">
          <button
            onClick={handleLoadMore}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-colors"
          >
            Load More Jobs
          </button>
        </div>
      )}
      
      {/* Status message for jobs count */}
      {totalPendingJobs > 0 && !isInitialLoading && (
        <div className="mt-2 text-center text-sm text-gray-500">
          {isSearching ? (
            <div>
              Showing {totalPendingJobs} jobs matching "{searchTerm}"
            </div>
          ) : (
            <div>
              Showing {totalPendingJobs} of {totalLoadedJobs} total assigned jobs
              {hasMore && (
                <span className="ml-1 text-gray-400">
                  • Click "Load More" to see additional jobs
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
